import { ofType } from 'redux-observable';
import { Observable, of } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { IWedding } from '@bridebook/models/source/models/Weddings.types';
import { AuthActionTypes } from '@bridebook/web/lib/auth/action-types';
import { setSearchLocationEnd } from '@bridebook/web/lib/search/actions';
import { Action, IEpicDeps } from '@bridebook/web/lib/types';
import { noopAction } from '@bridebook/web/lib/utils';
import { withCurrentWeddingCountry } from '@bridebook/web/lib/utils/operators/with-current-wedding-locale';
import {
  IUpdateWeddingAction,
  IUpdateWeddingFieldSuccessAction,
  WeddingActionTypes,
} from '@bridebook/web/lib/weddings/action-types';
import { Gazetteer } from '@bridebook/toolbox/src/gazetteer';
import formatArea from '../utils/format-prediction-area';
import { buildLocationSearchData } from '@bridebook/web/lib/search/utils/build-location-search-data';

export const updateSearchLocationOnWeddingLocationChange = (
  action$: Observable<IUpdateWeddingFieldSuccessAction>,
  { state$ }: IEpicDeps,
) =>
  action$.pipe(
    ofType(WeddingActionTypes.UPDATE_WEDDING_FIELD_SUCCESS),
    withCurrentWeddingCountry(state$),
    mergeMap(({ action, weddingCountry }) => {
      const { payload } = action;
      const hasLocationInPayload = !!(
        payload &&
        payload.name === 'location' &&
        payload.value?.location
      );

      if (!hasLocationInPayload) {
        return of(noopAction());
      }

      const location = payload.value.location as IWedding['location'];

      const { weddingLocation, area, countryCode, isPrediction, placeId } = buildLocationSearchData(
        { location, weddingCountry },
      );

      const countryName = countryCode ? Gazetteer.getCountryName(countryCode) : '';
      const searchText = weddingLocation || countryName;
      const searchType = isPrediction ? 'prediction' : 'autocomplete';

      return of(
        setSearchLocationEnd({
          status: 'initialized',
          draft: {
            searchType,
            searchText,
          },
          selected: {
            placeId,
            area,
            searchText,
            searchType,
            countryCode,
          },
        }),
      );
    }),
  );

// this is called only when search location was not loaded from local storage
export const setDefaultLocationToWeddingLocationWhenSearchLocationNotInitialized = (
  action$: Observable<IUpdateWeddingAction>,
  { state$ }: IEpicDeps,
) =>
  action$.pipe(
    ofType<Action>(WeddingActionTypes.UPDATE_WEDDING, AuthActionTypes.USER_SETUP_COMPLETED),
    withCurrentWeddingCountry(state$),
    mergeMap(({ action, state, weddingCountry }) => {
      const { wedding } = action.payload;
      const searchLocation = state.search.searchLocation;

      if (searchLocation.status === 'initialized' && searchLocation.draft.searchText) {
        return of(noopAction());
      }

      const location = wedding.location as IWedding['location'];

      const { weddingLocation, area, countryCode, isPrediction, placeId } = buildLocationSearchData(
        { location, weddingCountry },
      );

      const countryName = countryCode ? Gazetteer.getCountryName(countryCode) : '';
      const searchText = weddingLocation || countryName;
      const label = formatArea(weddingLocation) || countryName;
      const searchType = isPrediction ? 'prediction' : 'autocomplete';

      return of(
        setSearchLocationEnd({
          status: 'initialized',
          draft: {
            searchType,
            searchText,
          },
          selected: {
            placeId,
            searchType,
            searchText: label,
            area,
            countryCode,
          },
        }),
      );
    }),
  );
